import EventIcon from '@mui/icons-material/Event';
import Typography from '@mui/material/Typography';
import { TFunction, Trans } from 'react-i18next';
import { CarouselItem, FilterType, SignupCountryKeys } from '../types';
import { dateFilterOptions, statusFilterOptions } from './application';
import {
  buyerFilterOptions,
  fundingTypeFilterOptions,
  invoiceFilterOptions,
  noaStatusFilterOptions,
  paymentsCategoryFilterOptions,
  paymentsStatusFilterOptions,
} from './servicing';
import {
  CategoryIcon,
  GreyCalendarIcon,
  MiniBoxIcon,
  MoneyIcon,
  SearchIcon,
  StatusIcon,
} from '../components/Icons';
import { transactionsStatusFilterOptions, transactionsTransactionTypeFilterOptions } from './banking';
import { currencies } from '../utils/common';
import { CalendarIcon } from '@mui/x-date-pickers/icons';
import dayjs from 'dayjs';

export const allCompaniesTableFilters: FilterType[] = [
  {
    name: 'company',
    type: 'text',
    placeholder: 'admin:clientsPage.gridFilters.clientTitle',
    size: 3.5,
    Icon: <SearchIcon />,
  },
  {
    name: 'products',
    type: 'multipleSelect',
    placeholder: 'admin:clientsPage.gridFilters.productsFilterPlaceholder',
    size: 2.5,
    options: ['invoice_financing', 'banking', 'compliance'],
    tKey: `admin:clientsPage.gridFilters.productsSelectOptions`,
    Icon: <MiniBoxIcon />,
  },
  {
    name: 'subProducts',
    type: 'multipleSelect',
    placeholder: 'admin:clientsPage.gridFilters.subProductsFilterPlaceholder',
    size: 2.5,
    options: ['invoicing', 'bookkeeping', 'tax_compliance'],
    tKey: `admin:clientsPage.gridFilters.subProductsSelectOptions`,
    Icon: <MiniBoxIcon />,
  },
  {
    name: 'ordering',
    type: 'select',
    placeholder: 'admin:clientsPage.gridFilters.orderingTitle',
    size: 2.5,
    options: dateFilterOptions,
    tKey: 'admin:clientsPage.gridFilters.dateOptions',
    Icon: <GreyCalendarIcon />,
  },
];

export const invoiceFinancingCompaniesTableFilters: FilterType[] = [
  {
    name: 'company',
    type: 'text',
    placeholder: 'admin:clientsPage.gridFilters.clientTitle',
    size: 3.5,
    Icon: <SearchIcon />,
  },
  {
    name: 'status',
    type: 'select',
    placeholder: 'admin:clientsPage.gridFilters.statusFilterPlaceholder',
    size: 3.5,
    options: statusFilterOptions,
    tKey: 'admin:clientsPage.gridFilters.statusSelectOptions',
    Icon: <StatusIcon />,
  },
  {
    name: 'ordering',
    type: 'select',
    placeholder: 'admin:clientsPage.gridFilters.orderingTitle',
    size: 2,
    options: dateFilterOptions,
    tKey: 'admin:clientsPage.gridFilters.dateOptions',
    Icon: <GreyCalendarIcon />,
  },
  {
    name: 'deletedOnly',
    type: 'checkbox',
    title: 'admin:clientsPage.gridFilters.deletedOnlyTitle',
    size: 2,
  },
];

export const invoicesTableFilters: FilterType[] = [
  {
    name: 'invoice',
    type: 'text',
    title: 'servicing:invoicesTable.filters.invoiceNumber.title',
    placeholder: 'servicing:invoicesTable.filters.invoiceNumber.placeholder',
    size: 2,
    showLabel: false,
    Icon: <SearchIcon />,
  },
  {
    name: 'buyer',
    type: 'text',
    title: 'servicing:invoicesTable.filters.buyer.title',
    placeholder: 'servicing:invoicesTable.filters.buyer.placeholder',
    size: 2,
    showLabel: false,
    Icon: <SearchIcon />,
  },
  {
    name: 'status',
    type: 'select',
    title: 'servicing:invoicesTable.filters.invoiceStatus.title',
    size: 2.5,
    options: invoiceFilterOptions,
    tKey: (value) => `servicing:invoicesTable.tableColumns.invoiceStatusMap.${value}.label`,
    placeholder: 'servicing:invoicesTable.filters.invoiceStatus.placeholder',
    Icon: <StatusIcon />,
    showLabel: false,
  },
  {
    name: 'dates',
    type: 'dateRangeSingleField',
    title: '',
    placeholder: 'servicing:invoicesTable.filters.dates.placeholder',
    size: 4.5,
    showLabel: false,
    Icon: <CalendarIcon />,
  },
];

export const buyersTableFilters: FilterType[] = [
  {
    name: 'buyer',
    type: 'text',
    placeholder: 'servicing:buyerSummary.filters.buyer',
    size: 4,
    Icon: <SearchIcon />,
  },
  {
    name: 'noaStatusFilter',
    type: 'select',
    options: noaStatusFilterOptions,
    tKey: (value) => `servicing:buyerSummary.tableColumns.noaStatusMap.${value}`,
    placeholder: 'servicing:buyerSummary.filters.noaStatusFilter',
    size: 4,
    Icon: <StatusIcon />,
  },
  {
    name: 'status',
    type: 'select',
    options: buyerFilterOptions,
    tKey: (value) => `servicing:buyerSummary.buyerStatusMap.${value}`,
    placeholder: 'servicing:buyerSummary.filters.status',
    size: 3,
    Icon: <StatusIcon />,
  },
];

export const buyerCreditCheckTableFilters: FilterType[] = [
  {
    name: 'companyName',
    type: 'text',
    title: 'admin:clientsPage.buyerCreditCheck.gridFilters.companyNameTitle',
    placeholder: 'admin:clientsPage.buyerCreditCheck.gridFilters.genericFilterPlaceholder',
    size: 3,
  },
  {
    name: 'country',
    type: 'text',
    title: 'admin:clientsPage.buyerCreditCheck.gridFilters.countryTitle',
    placeholder: 'admin:clientsPage.buyerCreditCheck.gridFilters.genericFilterPlaceholder',
    size: 3,
  },
  {
    name: 'ordering',
    type: 'select',
    title: 'admin:clientsPage.buyerCreditCheck.gridFilters.orderingTitle',
    placeholder: 'admin:clientsPage.gridFilters.dateFilterPlaceholder',
    size: 3,
    options: dateFilterOptions,
    tKey: 'admin:clientsPage.gridFilters.dateOptions',
    Icon: <EventIcon />,
  },
];

export const fxRequestTableFilters: FilterType[] = [
  {
    name: 'ordering',
    type: 'select',
    title: 'admin:clientsPage.buyerCreditCheck.gridFilters.orderingTitle',
    placeholder: 'admin:clientsPage.gridFilters.dateFilterPlaceholder',
    size: 3,
    options: dateFilterOptions,
    tKey: 'admin:clientsPage.gridFilters.dateOptions',
    Icon: <EventIcon />,
  },
];

export const getProductsByCountry = (countryKey: SignupCountryKeys, t: TFunction) => {
  const defaultProducts: CarouselItem[] = [
    {
      image: '/images/products/financing.png',
      title: (
        <Typography variant="h4" sx={{ textAlign: 'center' }}>
          {t('products.financing.title')}
        </Typography>
      ),
      description: t('products.financing.description'),
    },
    {
      image: '/images/products/buyerCheck.png',
      title: (
        <Typography variant="h4" sx={{ textAlign: 'center' }}>
          {t('products.buyerCheck.title')}
        </Typography>
      ),
      description: t('products.buyerCheck.description'),
    },
    {
      image: '/images/products/llc.png',
      title: (
        <Trans t={t} i18nKey={'products.llc.title'}>
          <Typography variant="h4" sx={{ textAlign: 'center' }}>
            Establish an LLC
          </Typography>
          <Typography variant="h4" sx={{ textAlign: 'center' }}>
            in the US
          </Typography>
        </Trans>
      ),
      description: t('products.llc.description'),
    },
  ];
  switch (countryKey) {
    case 'mx':
      return [
        ...defaultProducts,
        {
          image: '/images/products/fx.png',
          title: (
            <Typography variant="h4" sx={{ textAlign: 'center' }}>
              {t('products.fx.title')}
            </Typography>
          ),
          description: t('products.fx.description'),
        },
        {
          image: '/images/products/insurance.png',
          title: (
            <Typography variant="h4" sx={{ textAlign: 'center' }}>
              {t('products.insurance.title')}
            </Typography>
          ),
          description: t('products.insurance.description'),
        },
      ];
    default:
      return defaultProducts;
  }
};

export const trackingTableFilters: FilterType[] = [
  {
    name: 'containerNumber',
    type: 'text',
    title: 'Container ID',
    placeholder: 'Enter a container ID',
    size: 5,
  },
  {
    name: 'dischargePort',
    type: 'text',
    title: 'Destination port',
    placeholder: 'Enter a destination port',
    size: 5,
  },
];

export const fundingsTableFilters: FilterType[] = [
  {
    name: 'transactionId',
    type: 'text',
    placeholder: 'servicing:fundings.filters.transactionId',
    size: 8.5,
    Icon: <SearchIcon />,
  },
  {
    name: 'paymentType',
    type: 'select',
    options: fundingTypeFilterOptions,
    tKey: (value) => `servicing:fundings.filters.paymentTypeMap.${value}`,
    placeholder: 'servicing:fundings.filters.paymentType',
    size: 2.5,
    Icon: <StatusIcon />,
  },
];

export const transactionsTableFilters: FilterType[] = [
  {
    name: 'search',
    type: 'text',
    title: '',
    placeholder: 'banking:connectedAccountDetails.transactions.filters.search.placeholder',
    size: 5,
    showLabel: false,
    Icon: <SearchIcon />,
  },
  {
    name: 'transactionType',
    type: 'select',
    size: 3.5,
    options: transactionsTransactionTypeFilterOptions,
    tKey: (value) => `banking:connectedAccountDetails.transactions.columns.transactionType.values.${value}`,
    placeholder: 'banking:connectedAccountDetails.transactions.filters.transactionType.placeholder',
    Icon: <CategoryIcon />,
    showLabel: false,
  },
  {
    name: 'currency',
    type: 'select',
    size: 3.5,
    options: Object.values(currencies),
    placeholder: 'banking:connectedAccountDetails.transactions.filters.currencies.placeholder',
    Icon: <MoneyIcon />,
    showLabel: false,
  },
  {
    name: 'dates',
    type: 'dateRangeSingleField',
    placeholder: 'banking:connectedAccountDetails.transactions.columns.dates.placeholder',
    size: 5,
    Icon: <CalendarIcon />,
    showLabel: false,
  },
  {
    name: 'status',
    type: 'select',
    title: '',
    size: 3.5,
    options: transactionsStatusFilterOptions,
    tKey: (value) => `banking:connectedAccountDetails.transactions.columns.status.values.${value}`,
    placeholder: 'banking:connectedAccountDetails.transactions.filters.status.placeholder',
    Icon: <StatusIcon />,
    showLabel: false,
  },
];

export const completedFinanceRequestTableFilters: FilterType[] = [
  {
    name: 'batchNumber',
    type: 'text',
    title: 'servicing:payments.filters.batchNumber.title',
    placeholder: 'servicing:payments.filters.batchNumber.placeholder',
    size: 4,
    showLabel: false,
    Icon: <SearchIcon />,
  },
  {
    name: 'postDate',
    type: 'dateRangeSingleField',
    title: 'servicing:payments.filters.dates.placeholder',
    placeholder: 'servicing:payments.filters.dates.placeholder',
    size: 4,
    showLabel: false,
    disableFuture: true,
    Icon: <CalendarIcon />,
    minDate: dayjs('2020-01-01'),
  },
];

export const paymentsTableFilters: FilterType[] = [
  {
    name: 'batchNumber',
    type: 'text',
    title: 'servicing:payments.filters.batchNumber.title',
    placeholder: 'servicing:payments.filters.batchNumber.placeholder',
    size: 4,
    showLabel: false,
    Icon: <SearchIcon />,
  },
  {
    name: 'buyer',
    type: 'text',
    title: 'servicing:payments.filters.buyer.title',
    placeholder: 'servicing:payments.filters.buyer.placeholder',
    size: 4,
    showLabel: false,
    Icon: <SearchIcon />,
  },
  {
    name: 'status',
    type: 'select',
    title: 'servicing:payments.filters.status.title',
    size: 4,
    options: paymentsStatusFilterOptions,
    tKey: (value) => `servicing:payments.tableColumns.statusMap.${value}`,
    placeholder: 'servicing:payments.filters.status.placeholder',
    Icon: <StatusIcon />,
    showLabel: false,
  },
  {
    name: 'category',
    type: 'select',
    title: 'servicing:payments.filters.category.title',
    size: 4,
    options: paymentsCategoryFilterOptions,
    tKey: (value) => `servicing:payments.tableColumns.categoryMap.${value}`,
    placeholder: 'servicing:payments.filters.category.placeholder',
    Icon: <CategoryIcon />,
    showLabel: false,
  },
  {
    name: 'postDate',
    type: 'dateRangeSingleField',
    title: 'servicing:payments.filters.dates.placeholder',
    placeholder: 'servicing:payments.filters.dates.placeholder',
    size: 4,
    showLabel: false,
    disableFuture: true,
    Icon: <CalendarIcon />,
    minDate: dayjs('2020-01-01'),
  },
];

export const transfersStatusFilterOptions = ['pending', 'approved'];

export const transfersCategoryFilterOptions = ['funding', 'release', 'return_of_funds'];

export const transfersTableFilters: FilterType[] = [
  {
    name: 'batchNumber',
    type: 'text',
    title: 'servicing:transfers.filters.batchNumber.title',
    placeholder: 'servicing:transfers.filters.batchNumber',
    size: 4,
    showLabel: false,
    Icon: <SearchIcon />,
  },
  {
    name: 'bank',
    type: 'text',
    title: 'servicing:transfers.filters.bank.title',
    placeholder: 'servicing:transfers.filters.bank',
    size: 4,
    showLabel: false,
    Icon: <SearchIcon />,
  },
  {
    name: 'transferCategory',
    type: 'select',
    title: 'servicing:transfers.filters.category.title',
    size: 4,
    options: transfersCategoryFilterOptions,
    tKey: (value) => `servicing:transfers.tableColumns.categoryMap.${value}`,
    placeholder: 'servicing:transfers.filters.category',
    Icon: <CategoryIcon />,
    showLabel: false,
  },
  {
    name: 'postDate',
    type: 'dateRangeSingleField',
    placeholder: 'servicing:transfers.filters.dates',
    size: 4,
    Icon: <CalendarIcon />,
    showLabel: false,
    disableFuture: true,
  },
  {
    name: 'amount',
    type: 'numberRange',
    placeholder: 'servicing:transfers.filters.amount.placeholder',
    size: 4,
    showLabel: false,
    Icon: <MoneyIcon />,
  },
];

export const bankingConnectedAccountsTableFilters: FilterType[] = [
  {
    name: 'search',
    type: 'text',
    title: 'Search',
    placeholder: 'Search',
    size: 5,
    showLabel: false,
    Icon: <SearchIcon />,
  },
  {
    name: 'status',
    type: 'select',
    title: 'Status',
    size: 3,
    options: ['CREATED', 'SUBMITTED', 'ACTIVE', 'SUSPENDED', 'ACTION_REQUIRED'],
    tKey: (value) => `banking:connectedAccounts.columns.status.values.${value}`,
    placeholder: 'Select a status',
    Icon: <CategoryIcon />,
    showLabel: false,
  },
  {
    name: 'onlyRfi',
    type: 'checkbox',
    title: 'banking:connectedAccounts.filters.onlyRfi',
    size: 2,
  },
];

export const FACTORING_SUBMITTED_SCHEDULE_OF_ACCOUNT_TYPE = 'SUBMITTED_SCHEDULE_OF_ACCOUNT';
export const FACTORING_NEW_SOA_ASSET_TYPE = 'SOA_SIGNED_NEW_DOCUMENT';

export const revolvingCreditLineAdminCreditLinesTableFilters: FilterType[] = [
  {
    name: 'search',
    type: 'text',
    title: '',
    placeholder: 'revolvingCreditLine:creditLines.filters.search.placeholder',
    size: 7,
    showLabel: false,
    Icon: <SearchIcon />,
  },
  {
    name: 'status',
    type: 'select',
    title: '',
    size: 3,
    options: ['active', 'pending', 'blocked', 'inactive'],
    tKey: (value) => `revolvingCreditLine:creditLines.status.values.${value}`,
    placeholder: 'revolvingCreditLine:creditLines.filters.status.placeholder',
    Icon: <StatusIcon />,
    showLabel: false,
  },
];

export const revolvingCreditLineAdminWithdrawalsTableFilters: FilterType[] = [
  {
    name: 'search',
    type: 'text',
    title: '',
    placeholder: 'revolvingCreditLine:withdrawals.filters.search.placeholder',
    size: 3,
    showLabel: false,
    Icon: <SearchIcon />,
  },
  {
    name: 'status',
    type: 'select',
    title: '',
    size: 3,
    options: ['pending', 'requested', 'approved', 'deposited', 'cancelled', 'due'],
    tKey: (value) => `revolvingCreditLine:withdrawals.status.values.${value}`,
    placeholder: 'revolvingCreditLine:withdrawals.filters.status.placeholder',
    Icon: <StatusIcon />,
    showLabel: false,
  },
  {
    name: 'dates',
    type: 'dateRangeSingleField',
    placeholder: 'revolvingCreditLine:withdrawals.filters.dates.placeholder',
    size: 4,
    Icon: <CalendarIcon />,
    showLabel: false,
    disableFuture: true,
  },
];

export const revolvingCreditLineAdminPaymentsTableFilters: FilterType[] = [
  {
    name: 'search',
    type: 'text',
    title: '',
    placeholder: 'revolvingCreditLine:payments.filters.search.placeholder',
    size: 3,
    showLabel: false,
    Icon: <SearchIcon />,
  },
  {
    name: 'status',
    type: 'select',
    title: '',
    size: 3,
    options: ['pending', 'processing', 'applied', 'reverse', 'cancelled'],
    tKey: (value) => `revolvingCreditLine:payments.status.values.${value}`,
    placeholder: 'revolvingCreditLine:payments.filters.status.placeholder',
    Icon: <StatusIcon />,
    showLabel: false,
  },
  {
    name: 'dates',
    type: 'dateRangeSingleField',
    placeholder: 'revolvingCreditLine:payments.filters.dates.placeholder',
    size: 4,
    Icon: <CalendarIcon />,
    showLabel: false,
    disableFuture: true,
  },
];

export const revolvingCreditLineAdminInterestPaymentsTableFilters: FilterType[] = [
  {
    name: 'search',
    type: 'text',
    title: '',
    placeholder: 'revolvingCreditLine:payments.filters.search.placeholder',
    size: 4,
    showLabel: false,
    Icon: <SearchIcon />,
  },
  {
    name: 'status',
    type: 'select',
    title: '',
    size: 2,
    options: ['pending', 'processing', 'applied', 'reverse', 'cancelled', 'paid'],
    tKey: (value) => `revolvingCreditLine:payments.status.values.${value}`,
    placeholder: 'revolvingCreditLine:payments.filters.status.placeholder',
    Icon: <StatusIcon />,
    showLabel: false,
  },
  {
    name: 'month',
    type: 'month',
    size: 4,
    Icon: <CalendarIcon />,
    showLabel: false,
    disableFuture: true,
    clearable: true,
  },
];

export const HELP_CENTER_URL = 'https://ayuda.marcofi.com';
